import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery, useQuery } from "@tanstack/vue-query";
import {
  FilterApi,
  type Product,
  ProductBidsApi,
  type ProductList,
  ProductsApi,
  type ProductsApiV2ProductsProductIdentifierCostEstimateGetRequest,
  ProductSorting,
  ProductStatus,
  SalesMethod,
} from "mws-ts-v2-sdk";
import { unref } from "vue";

import { getUniversalAxios } from "~/utils/axios/getUniversalAxios";

const getProductsApi = () => {
  return new ProductsApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    getUniversalAxios(),
  );
};

const getProductBidsApi = () => {
  return new ProductBidsApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    getUniversalAxios(),
  );
};

const getProductsFilterApi = () => {
  return new FilterApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    getUniversalAxios(),
  );
};

export const productQueryKeys = createQueryKeys("products", {
  details: (
    productIdentifier: MaybeRefOrGetter<string>,
    platformId: MaybeRef<string>,
  ) => ({
    queryKey: [productIdentifier],
    queryFn: async () => {
      const response = await getProductsApi().v2ProductsProductIdentifierGet({
        productIdentifier: toValue(productIdentifier),
        xPlatformKey: unref(platformId),
      });

      return response.data;
    },
  }),
  filters: (
    platformId: MaybeRef<string>,
    params: MaybeRef<{
      eventIdentifier?: string;
      searchTerm?: string;
      categoryIdentifier?: string;
    }>,
  ) => ({
    queryKey: [params],
    queryFn: async () => {
      const unreffedParams = unref(params);
      const response = await getProductsFilterApi().v2FilterProductsGet({
        eventId: unreffedParams.eventIdentifier,
        searchTerm: unreffedParams.searchTerm,
        categoryId: unreffedParams.categoryIdentifier,
        xPlatformKey: unref(platformId),
      });

      return response.data;
    },
  }),
  detailsByCertificateNumber: (
    certificateNumber: MaybeRef<string>,
    platformId: MaybeRef<string>,
  ) => ({
    queryKey: [certificateNumber],
    queryFn: async () => {
      const response =
        await getProductsApi().v2ProductsCertificateCertificateNumberGet({
          certificateNumber: unref(certificateNumber),
          xPlatformKey: unref(platformId),
        });

      return response.data;
    },
  }),
  bids: (productId: MaybeRef<string>, platformId: MaybeRef<string>) => ({
    queryKey: [productId],
    queryFn: async () => {
      const response = await getProductBidsApi().v2ProductsProductIdBidsGet({
        productId: unref(productId),
        xPlatformKey: unref(platformId),
      });

      return response.data;
    },
  }),
  estimatedCost: (
    request: MaybeRefOrGetter<ProductsApiV2ProductsProductIdentifierCostEstimateGetRequest>,
  ) => ({
    queryKey: [request],
    queryFn: async () => {
      const response =
        await getProductsApi().v2ProductsProductIdentifierCostEstimateGet(
          toValue(request),
        );

      return response.data;
    },
  }),
});

export const useProductDetailsQuery = (
  productIdentifier: MaybeRef<string>,
  options?: {
    enabled?: MaybeRef<boolean>;
    autoRefetch?: boolean;
    redirectNotFound?: boolean;
    suspenseWithHydration?: boolean;
  },
) => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const query = useQuery({
    ...productQueryKeys.details(productIdentifier, platformId),
    enabled: options?.enabled ?? true,
    // refetch every minute - 60000ms
    refetchInterval: options?.autoRefetch && 60000,
    suspense: options?.suspenseWithHydration,
    ...(options?.redirectNotFound && { ...notFoundQueryOptions }),
  });

  if (options?.suspenseWithHydration) {
    useSuspenseWithHydration({
      suspenseFn: query.suspense,
      queryKey: productQueryKeys.details(productIdentifier, platformId)
        .queryKey,
    });
  }

  return query;
};

type ProductListFilters = {
  eventIdentifier?: string | null; // null instead of undefined so that the query keys are consistent when a filter is not provided
  categorySlug?: string | null;
  salesMethod?: SalesMethod | null;
  categoryIds?: string[];
  productIds?: string[];
  priceFrom?: string;
  priceTo?: string;
  status?: ProductStatus;
};

export const useInfiniteProductsList = <SelectedProducts = Product[]>(
  params: {
    filters: MaybeRefOrGetter<ProductListFilters>;
    sorting?: MaybeRefOrGetter<ProductSorting | undefined>;
    pageSize?: MaybeRefOrGetter<number>;
  },
  options?: {
    enabled?: Ref<boolean>;
    select?: (products: Product[]) => SelectedProducts;
    suspenseWithHydration?: boolean;
  },
) => {
  const { filters, sorting, pageSize = 20 } = params;

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const { activeCurrency } = useActiveCurrency();

  const queryKey = ["products", "infiniteList", filters, sorting];
  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const unreffedFilters = toValue(filters);
      const unreffedPageSize = toValue(pageSize);

      const onlyReturnNotEnded =
        unreffedFilters.salesMethod === SalesMethod.FixedPrice;
      const sortByCreateDate =
        unreffedFilters.salesMethod === SalesMethod.FixedPrice;

      const response = await getProductsApi().v2ProductsPaginatedGet({
        xPlatformKey: platformId.value,
        eventId: unreffedFilters.eventIdentifier ?? undefined,
        categorySlug: unreffedFilters.categorySlug ?? undefined,
        salesMethod: unreffedFilters.salesMethod ?? undefined,
        onlyReturnNotEnded,
        sortByCreateDate,
        categoryIds: unreffedFilters.categoryIds?.join(",") ?? undefined,
        productIds: unreffedFilters.productIds?.join(",") ?? undefined,
        minimumPrice: unreffedFilters.priceFrom
          ? Number(unreffedFilters.priceFrom) * 100
          : undefined,
        maximumPrice: unreffedFilters.priceTo
          ? Number(unreffedFilters.priceTo) * 100
          : undefined,
        productStatus: unreffedFilters.status ?? undefined,
        currencyCode: toValue(activeCurrency.value.isoCurrencyCode),
        sortProductsBy: toValue(sorting) ?? undefined,
        // Typecast page param to make sure we can spread over options
        offset: pageParam as number,
        pageSize: unreffedPageSize,
      });

      return response.data;
    },
    staleTime: 45000,
    initialPageParam: 0,
    getNextPageParam: (lastPage: ProductList) => {
      const unreffedPageSize = toValue(pageSize);
      const nextPageOffset = lastPage.offset + toValue(unreffedPageSize);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
    placeholderData: (data) => data,
    select: (data) => {
      const flattenedProductsData =
        data.pages.flatMap((page) => page.results) ?? [];

      return {
        data: options?.select
          ? options.select(flattenedProductsData)
          : flattenedProductsData,
        totalCount: data.pages?.[0]?.totalCount ?? 0,
        ...data,
      };
    },
    enabled: options?.enabled,
    suspense: options?.suspenseWithHydration,
  });

  if (options?.suspenseWithHydration) {
    useSuspenseWithHydration({
      suspenseFn: query.suspense,
      queryKey,
    });
  }

  return query;
};
